import { useCallback } from 'react';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

type OnErrorRender = (action: MessageAction, args: { error: Error; info: React.ErrorInfo }) => void;

export type MessageActionRenderAnalytics = {
	onErrorRender?: OnErrorRender;
	onSuccessRender?: (action: MessageAction) => void;
};

export const useMessageActionsAnalytics = ({
	recipientAgentNamedId,
	messageId,
}: {
	recipientAgentNamedId?: string;
	messageId: string;
}): MessageActionRenderAnalytics => {
	const { sendAnalyticsEvent } = useAnalytics();

	const onErrorRender: OnErrorRender = useCallback(
		(action, { error }) => {
			sendAnalyticsEvent({
				eventType: 'track',
				action: 'error',
				actionSubject: 'rovoChatMessageAction',
				attributes: {
					recipientAgentNamedId,
					actionKey: action.key,
					messageId,
				},
			});
			logException({
				name: 'rovoChatMessageAction',
				exception: error,
				tags: {
					component: 'useActionRenderer',
					actionKey: action.key,
					invocationId: action.invocationId,
					actionId: String(action.actionId),
					status: String(action.status),
				},
			});
		},
		[sendAnalyticsEvent, recipientAgentNamedId, messageId],
	);

	const onSuccessRender = useCallback(
		(action: MessageAction) => {
			if (!action || action.status !== 'planned') {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'screen',
				name: 'rovoChatMessageAction',
				attributes: {
					recipientAgentNamedId,
					actionKey: action.key,
				},
			});
		},
		[sendAnalyticsEvent, recipientAgentNamedId],
	);

	return {
		onErrorRender,
		onSuccessRender,
	};
};
